import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';

import { APP_ENV } from '@shared/data-access/app-config';
import { PAGES_URLS } from '@shared/data-access/constants';

import { App } from '@capacitor/app';

const NATIVE_SCHEME_PREFIX = 'com.ireckonu.staffapp://';

@Injectable({ providedIn: 'root' })
export class NativeAuthService {
  private readonly router = inject(Router);
  private readonly appEnv = inject(APP_ENV);

  init(): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    void App.addListener('appUrlOpen', (event) => {
      this.handleUrl(event.url);
    });
  }

  /** Logs only in non-production builds (gated on `APP_ENV.production`). */
  private log(...args: unknown[]): void {
    if (this.appEnv.production) {
      return;
    }
    // eslint-disable-next-line no-console
    console.log(...args);
  }

  private handleUrl(url: string): void {
    this.log('[DEEP-LINK] appUrlOpen received:', url);

    // Match com.ireckonu.staffapp://login or com.ireckonu.staffapp://iru.login
    if (!url.startsWith(NATIVE_SCHEME_PREFIX)) {
      this.log(
        '[DEEP-LINK] URL does not match expected scheme, ignoring:',
        NATIVE_SCHEME_PREFIX,
      );
      return;
    }

    // Extract the host (e.g. "iru.login" or "login") to verify it ends with "login"
    const afterScheme = url.slice(NATIVE_SCHEME_PREFIX.length);
    const hostEnd = afterScheme.search(/[/?#]/);
    const host = hostEnd === -1 ? afterScheme : afterScheme.slice(0, hostEnd);
    if (!host.endsWith('login')) {
      this.log('[DEEP-LINK] host does not end with "login", ignoring:', host);
      return;
    }

    // Tokens may come back as query params or fragment — check both.
    // e.g. com.ireckonu.staffapp://login?accessToken=xxx&refreshToken=yyy
    //   or com.ireckonu.staffapp://login#accessToken=xxx&refreshToken=yyy
    //
    // Defensive: if the IDP appended params with '?' instead of '&' (e.g.
    // because our redirectUrl already contained a '?'), normalize by replacing
    // every '?' after the first with '&' before parsing.
    const firstQuestion = url.indexOf('?');
    const normalized =
      firstQuestion === -1
        ? url
        : url.slice(0, firstQuestion + 1) +
          url.slice(firstQuestion + 1).replace(/\?/g, '&');

    const questionIndex = normalized.indexOf('?');
    const hashIndex = normalized.indexOf('#');

    let accessToken: string | null = null;
    let refreshToken: string | null = null;

    if (questionIndex !== -1) {
      const queryString = normalized.slice(
        questionIndex + 1,
        hashIndex !== -1 ? hashIndex : undefined,
      );
      const params = new URLSearchParams(queryString);
      accessToken = params.get('accessToken');
      refreshToken = params.get('refreshToken');
    }

    if (!accessToken && hashIndex !== -1) {
      const fragment = normalized.slice(hashIndex + 1);
      const params = new URLSearchParams(fragment);
      accessToken = params.get('accessToken');
      refreshToken = params.get('refreshToken');
    }

    if (accessToken) {
      localStorage.setItem('accessToken', accessToken);
    }
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }

    this.log('[DEEP-LINK] tokens parsed:', {
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
    });

    void Browser.close();
    void this.router.navigate([PAGES_URLS.HotelReservations]);
  }
}
